/**
 * SEO utilities for listing pages.
 * Per-category meta title and description builders with brand alias support.
 *
 * Each L1 category has its own title/description formula based on its
 * hierarchy pattern (L2=brand vs L2=type) and available publicData fields.
 * Categories without a custom builder fall back to the generic approach.
 */

import { GMC_TARGET_COUNTRIES, areCountriesInSameContinent } from '../../shared/countryData';
import { subUnitDivisors } from '../../config/settingsCurrency';

// ─── Constants ───────────────────────────────────────────────────────────────

// L1 categories where L2 represents the brand/manufacturer (and L3 = model).
// For all other L1 categories, L2 is a product type and L3 (if present) is the brand.
const L2_IS_BRAND = new Set([
  'rigs',
  'main-canopies',
  'reserves',
  'aad',
  'wingsuits',
  'audibles',
]);

// Generic category type labels used by the default description builder.
const CATEGORY_TYPE_LABELS = {
  'main-canopies': 'main canopy',
  'rigs': 'skydiving container',
  'reserves': 'reserve canopy',
  'complete-systems': 'skydiving complete system',
  'aad': 'skydiving AAD',
  'altimeters': 'skydiving altimeter',
  'audibles': 'skydiving audible',
  'wingsuits': 'wingsuit',
  'tracksuits': 'skydiving tracksuit',
  'helmets-and-equipment': 'skydiving helmet',
  'clothing': 'skydiving suit',
  'videography-photography': 'skydiving camera gear',
  'other-gear': 'skydiving accessory',
  'base': 'BASE gear',
};

// Rig brand values from Complete Systems publicData field → display names.
const SYSTEM_RIG_BRANDS = {
  'systems-aerodyne': 'Aerodyne',
  'systems-mirage': 'Mirage',
  'systems-rigging-innovation': 'Rigging Innovations',
  'systems-sife': 'SIFE',
  'systems-upt': 'UPT',
};

// Brand alias map: display name (as produced by getBrandDisplay) → [aliases].
// Used for case-insensitive de-duplication and stripping in seller titles.
const BRAND_ALIASES = {
  // Containers
  'Sun Path': ['SunPath'],
  'Rigging Innovations': ['RI'],
  'NA Aerodynamics': ['North American Aerodynamics'],
  // Main Canopies
  'PD': ['Performance Designs', 'Performance Design'],
  'JYRO': ['Icarus Canopies', 'NZ Aerosports'],
  'Fluid Wings': ['FW'],
  'Skylark': ['Skylark Parachutes'],
  // Altimeters / Audibles
  'AON2': ['AO(N2)', 'AON 2', 'AON-2'],
  'Alti-2': ['Alti2', 'Alti 2'],
  'Deep and Steep': ['DeepAndSteep', 'Deep&Steep', 'DeepnSteep'],
  'L&B': ['LnB', 'LB'],
  // Wingsuits
  'Alien Suits': ['aliensuits', 'aliensuit', 'alien suit'],
  'Fly Your Body': ['flyyourbody'],
  'S Fly': ['s-fly', 'sfly'],
  'TonySuits': ['tony suits'],
  // Suits / Clothing
  'Formation': ['FS', 'RW'],
};

// Short brand form shown in titles/descriptions, keyed on the raw category
// label. Mirrors TITLE_BRAND_DISPLAY in scripts/merchantSync.js (GMC feed).
const TITLE_BRAND_DISPLAY = {
  'Performance Designs': 'PD',
  'JYRO (Icarus Canopies)': 'JYRO',
  'Advanced Aerospace Designs (Vigil)': 'Vigil',
  'Cypres': 'CYPRES',
  'AO(N2)': 'AON2',
  'DeepAndSteep': 'Deep and Steep',
};

// ─── Shared Utilities ────────────────────────────────────────────────────────

/**
 * Resolve category hierarchy from listing publicData.
 * @returns {Array<{id: string, name: string}>} ordered trail of category levels
 */
export const getCategoryTrail = (publicData = {}, categoryConfiguration = {}) => {
  const categories = categoryConfiguration.categories || [];
  const levelKeys = categoryConfiguration.categoryLevelKeys || [];
  const trail = [];
  let currentLevel = categories;

  for (const levelKey of levelKeys) {
    const selectedId = publicData[levelKey];
    if (!selectedId || !currentLevel) break;
    const match = currentLevel.find(cat => cat.id === selectedId);
    if (!match) break;
    trail.push({ id: match.id, name: match.name });
    currentLevel = match.subcategories || [];
  }

  return trail;
};

/**
 * Shorten a category display name. Drops a " / variant" tail and a trailing
 * "(annotation)", e.g. "JYRO (Icarus Canopies)" → "JYRO".
 * Returns null for "Other" categories. Mirrors merchantSync.js.
 */
const getShortName = name => {
  if (!name) return null;
  if (name.toLowerCase() === 'other') return null;
  return (
    name
      .split(' / ')[0]
      .replace(/\s*\([^)]*\)\s*$/, '')
      .trim() || null
  );
};

/**
 * Brand display form for titles/descriptions: explicit GMC override first,
 * short name otherwise. Use for trail entries that represent a brand.
 */
const getBrandDisplay = name =>
  name ? TITLE_BRAND_DISPLAY[name] || getShortName(name) : null;

/**
 * Check if text contains a term (case-insensitive).
 * Uses word boundaries for purely alphanumeric terms to avoid partial matches.
 */
const containsTerm = (text, term) => {
  if (!text || !term) return false;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const hasSpecialChars = /[^a-zA-Z0-9\s]/.test(term);
  const pattern = hasSpecialChars ? escaped : `\\b${escaped}\\b`;
  return new RegExp(pattern, 'i').test(text);
};

/**
 * Check if title contains a term or any of its registered aliases.
 */
const titleContainsTerm = (title, term) => {
  if (!title || !term) return false;
  if (containsTerm(title, term)) return true;
  const aliases = BRAND_ALIASES[term] || [];
  return aliases.some(a => containsTerm(title, a));
};

/**
 * Strip a single term from text (case-insensitive). Cleans up double spaces.
 * Uses word boundaries for purely alphanumeric terms.
 */
const stripTerm = (text, term) => {
  if (!text || !term) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const hasSpecialChars = /[^a-zA-Z0-9\s]/.test(term);
  const pattern = hasSpecialChars ? escaped : `\\b${escaped}\\b`;
  return text
    .replace(new RegExp(pattern, 'gi'), '')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

/**
 * Strip a brand name and all its aliases from text.
 */
const stripBrandFromTitle = (title, brandName) => {
  if (!title || !brandName) return title;
  let result = stripTerm(title, brandName);
  const aliases = BRAND_ALIASES[brandName] || [];
  for (const alias of aliases) {
    result = stripTerm(result, alias);
  }
  return result;
};

const getConditionLabel = condition =>
  condition === 'condition-new' ? 'New' : 'Used';

/** Format location for meta description text. */
const formatLocation = location => {
  if (location?.address) return `Located in ${location.address}.`;
  if (location?.country) return `Located in ${location.country}.`;
  return '';
};

/**
 * Find title enrichment for the generic builder.
 * Walks from deepest category to shallowest (skipping L1), returns the first
 * short name not already present in the seller title.
 */
const getTitleEnrichment = (categoryTrail, title, publicData = {}) => {
  for (let i = categoryTrail.length - 1; i >= 1; i--) {
    const shortName = getBrandDisplay(categoryTrail[i].name);
    if (!shortName) continue;
    if (!titleContainsTerm(title, shortName)) {
      return shortName;
    }
  }
  // Fallback: rig brand from publicData for Complete Systems
  const rigBrandKey = publicData.systems_brand_rig;
  if (rigBrandKey && SYSTEM_RIG_BRANDS[rigBrandKey]) {
    const brandName = SYSTEM_RIG_BRANDS[rigBrandKey];
    if (!titleContainsTerm(title, brandName)) {
      return brandName;
    }
  }
  return null;
};

/**
 * Extract brand and model for schema.org Product structured data.
 */
const getBrandAndModel = (categoryTrail, publicData = {}) => {
  if (categoryTrail.length === 0) return { brand: null, model: null };
  const l1Id = categoryTrail[0].id;
  const l2IsBrand = L2_IS_BRAND.has(l1Id);

  if (l2IsBrand && categoryTrail.length >= 3) {
    return { brand: getBrandDisplay(categoryTrail[1].name), model: categoryTrail[2].name };
  } else if (l2IsBrand && categoryTrail.length >= 2) {
    return { brand: getBrandDisplay(categoryTrail[1].name), model: null };
  } else if (!l2IsBrand && categoryTrail.length >= 3) {
    return { brand: getBrandDisplay(categoryTrail[2].name), model: null };
  }

  const rigBrandKey = publicData.systems_brand_rig;
  if (rigBrandKey && SYSTEM_RIG_BRANDS[rigBrandKey]) {
    return { brand: SYSTEM_RIG_BRANDS[rigBrandKey], model: null };
  }

  return { brand: null, model: null };
};

// ─── Generic / Fallback Builders ─────────────────────────────────────────────

/** Generic title: [Used/New] [Enrichment?] {Seller Title} | Avento */
const defaultTitle = (title, publicData, trail) => {
  const condition = getConditionLabel(publicData.condition);
  const enrichment = getTitleEnrichment(trail, title, publicData);
  const parts = [condition];
  if (enrichment) parts.push(enrichment);
  parts.push(title);
  return `${parts.join(' ')} | Avento`;
};

/**
 * Generic description builder.
 * @param {string|null} typeLabel — override for CATEGORY_TYPE_LABELS. Pass null to suppress.
 */
const defaultDescription = (title, publicData, trail, formattedPrice, typeLabel) => {
  const condition = getConditionLabel(publicData.condition);
  const enrichment = getTitleEnrichment(trail, title, publicData);

  const headerParts = [condition];
  if (enrichment) headerParts.push(enrichment);
  headerParts.push(title);
  const header = headerParts.join(' ');

  const l1Id = trail.length > 0 ? trail[0].id : null;
  const effectiveTypeLabel =
    typeLabel !== undefined ? typeLabel : (l1Id ? CATEGORY_TYPE_LABELS[l1Id] || null : null);

  const details = [];

  // Size — field-based detection, combined with type label when natural
  let typeLabelUsed = false;
  if (publicData.main_reserve_size) {
    const sizePart = `${publicData.main_reserve_size} sqft`;
    details.push(effectiveTypeLabel ? `${sizePart} ${effectiveTypeLabel}` : sizePart);
    typeLabelUsed = !!effectiveTypeLabel;
  } else if (publicData.completesystem_main_canopy_size) {
    details.push(`main canopy ${publicData.completesystem_main_canopy_size} sqft`);
  } else if (publicData.allowedRangeMin && publicData.allowedRangeMax) {
    details.push(
      `holds ${publicData.allowedRangeMin}\u2013${publicData.allowedRangeMax} sqft`
    );
  } else if (publicData.helmets_size) {
    details.push(`size ${publicData.helmets_size}`);
  }

  // Category type context (if not already combined with size)
  if (effectiveTypeLabel && !typeLabelUsed) {
    details.push(effectiveTypeLabel);
  }

  // Jumps
  const jumps =
    publicData.number_of_jumps ||
    publicData.helmets_nr_of_jumps ||
    publicData.system_main_jumpnr;
  if (jumps) details.push(`${jumps} jumps`);

  // Year
  const year = publicData.year || publicData.year_rig;
  if (year) details.push(`DOM ${year}`);

  let desc = header;
  if (details.length > 0) desc += `, ${details.join(', ')}`;
  desc += '.';

  if (formattedPrice) desc += ` ${formattedPrice}.`;

  const loc = formatLocation(publicData.location);
  if (loc) desc += ` ${loc}`;

  desc += ' Buy with safe payments on Avento.';
  return desc;
};

// ─── Per-Category Title Builders ─────────────────────────────────────────────

const TITLE_BUILDERS = {
  // Containers: Used {Brand} {Model} {Year} | {Seller Title}
  // Reverts to generic when brand (L2) is "Other" or missing.
  'rigs': (title, pd, trail) => {
    const brand = getBrandDisplay(trail[1]?.name);
    if (!brand) return defaultTitle(title, pd, trail);
    const model = getShortName(trail[2]?.name);
    const year = pd.year;
    const parts = ['Used', brand];
    if (model) parts.push(model);
    if (year) parts.push(String(year));
    return `${parts.join(' ')} | ${title}`;
  },

  // Main Canopies: Used {Brand} {Model} {Size} sqft | Avento
  // Falls back to seller title if brand or model is Other/missing.
  'main-canopies': (title, pd, trail) => {
    const brand = getBrandDisplay(trail[1]?.name);
    const model = getShortName(trail[2]?.name);
    if (!brand || !model) return `Used ${title} | Avento`;
    const parts = ['Used', brand, model];
    if (pd.main_reserve_size) parts.push(`${pd.main_reserve_size} sqft`);
    return `${parts.join(' ')} | Avento`;
  },

  // Reserves: Used {Brand} {Model} {Size} sqft | Avento
  // Performance Designs → "PD". Avoids "PD PD Reserve" duplication.
  'reserves': (title, pd, trail) => {
    const brand = getBrandDisplay(trail[1]?.name);
    const model = getShortName(trail[2]?.name);
    if (!brand && !model) return `Used ${title} | Avento`;
    const parts = ['Used'];
    // If model already starts with brand name, skip the brand prefix
    if (brand && model && model.toLowerCase().startsWith(brand.toLowerCase())) {
      parts.push(model);
    } else {
      if (brand) parts.push(brand);
      if (model) parts.push(model);
    }
    if (pd.main_reserve_size) parts.push(`${pd.main_reserve_size} sqft`);
    return `${parts.join(' ')} | Avento`;
  },

  // AADs: Used {Model L3} {Year} | Avento — no brand in title
  'aad': (title, pd, trail) => {
    const model = getShortName(trail[2]?.name);
    if (!model) return `Used ${title} | Avento`;
    const parts = ['Used', model];
    if (pd.year) parts.push(String(pd.year));
    return `${parts.join(' ')} | Avento`;
  },

  // Altimeters: Digital → add "Digital Altimeter" in title. Others → generic.
  'altimeters': (title, pd, trail) => {
    const l2Id = trail[1]?.id;
    if (l2Id === 'altimeters-digital') {
      const condition = getConditionLabel(pd.condition);
      const brand = getBrandDisplay(trail[2]?.name);
      const parts = [condition];
      if (brand && !titleContainsTerm(title, brand)) parts.push(brand);
      parts.push(title);
      if (!title.toLowerCase().includes('digital altimeter')) {
        parts.push('Digital Altimeter');
      }
      return `${parts.join(' ')} | Avento`;
    }
    return defaultTitle(title, pd, trail);
  },

  // Audibles: add "Audible Altimeter" in title
  'audibles': (title, pd, trail) => {
    const condition = getConditionLabel(pd.condition);
    const brand = getBrandDisplay(trail[1]?.name);
    const parts = [condition];
    if (brand && !titleContainsTerm(title, brand)) parts.push(brand);
    parts.push(title);
    if (!title.toLowerCase().includes('audible')) {
      parts.push('Audible Altimeter');
    }
    return `${parts.join(' ')} | Avento`;
  },

  // Wingsuits: {Brand} {Seller Title*} Wingsuit | Avento
  // No "Used" prefix. Strips brand and "Wingsuit" from seller title.
  // Reverts to generic when brand (L2) is "Other" or missing.
  'wingsuits': (title, pd, trail) => {
    const brand = getBrandDisplay(trail[1]?.name);
    if (!brand) return defaultTitle(title, pd, trail);
    let clean = title;
    clean = stripBrandFromTitle(clean, brand);
    clean = stripTerm(clean, 'wingsuit');
    const parts = [brand];
    if (clean) parts.push(clean);
    parts.push('Wingsuit');
    return `${parts.join(' ')} | Avento`;
  },

  // Tracksuits: {Brand L3} {Seller Title*} {L2 Type} Tracksuit | Avento
  // No "Used" prefix. Strips brand and "Tracksuit" from seller title.
  // Reverts to generic when brand (L3) is "Other" or missing.
  'tracksuits': (title, pd, trail) => {
    const l2Name = trail[1]?.name; // "One-piece" or "Two-piece"
    const brand = getBrandDisplay(trail[2]?.name);
    if (!brand) return defaultTitle(title, pd, trail);
    let clean = title;
    clean = stripBrandFromTitle(clean, brand);
    clean = stripTerm(clean, 'tracksuit');
    const parts = [brand];
    if (clean) parts.push(clean);
    if (l2Name) parts.push(l2Name);
    parts.push('Tracksuit');
    return `${parts.join(' ')} | Avento`;
  },

  // Helmets: glasses → plain seller title, others → generic
  'helmets-and-equipment': (title, pd, trail) => {
    if (trail[1]?.id === 'helmets-and-equipment-glasses') {
      return `Used ${title} | Avento`;
    }
    return defaultTitle(title, pd, trail);
  },

  // Suits: Used skydiving {L2 Discipline} Apparel | {Seller Title}
  'clothing': (title, pd, trail) => {
    const l2Name = getShortName(trail[1]?.name);
    const parts = ['Used', 'skydiving'];
    if (l2Name) parts.push(l2Name);
    parts.push('Apparel');
    return `${parts.join(' ')} | ${title}`;
  },

  // Video/Photo: {Seller Title} Skydiving Camera {L2} | Avento
  // No "Used" prefix. Omits L2 if "Cameras" (redundant).
  'videography-photography': (title, pd, trail) => {
    const l2Name = trail[1]?.name;
    const parts = [title, 'Skydiving', 'Camera'];
    if (l2Name && l2Name !== 'Cameras') parts.push(l2Name);
    return `${parts.join(' ')} | Avento`;
  },

  // Other Gear: Buy {Seller Title} | Used Skydiving {L2 Type}
  'other-gear': (title, pd, trail) => {
    const l2Name = getShortName(trail[1]?.name);
    if (l2Name) return `Buy ${title} | Used Skydiving ${l2Name}`;
    return `Buy ${title} | Avento`;
  },
};

// ─── Per-Category Description Builders ───────────────────────────────────────

const DESC_BUILDERS = {
  // Containers: custom CTA with "container, rig, harness", includes weight/height
  'rigs': (title, pd, trail, price) => {
    const brand = getBrandDisplay(trail[1]?.name);
    const intro = brand ? `Buy used ${brand} ${title}` : `Buy used ${title}`;
    const details = [];
    if (pd.year) details.push(`DOM ${pd.year}`);
    if (pd.number_of_jumps) details.push(`${pd.number_of_jumps} jumps`);
    if (price) details.push(price);

    let desc = intro;
    if (details.length > 0) desc += `, ${details.join(', ')}`;
    desc += '.';

    const loc = formatLocation(pd.location);
    if (loc) desc += ` ${loc}`;

    const body = [];
    if (pd.weight_rigs) body.push(`${pd.weight_rigs} kg`);
    if (pd.height_rigs) body.push(`${pd.height_rigs} cm`);
    if (body.length > 0) desc += ` For ${body.join(', ')}.`;

    desc += ' Buy container, rig, harness with safe payments on Avento.';
    return desc;
  },

  // Main Canopies: fully structured, includes colors
  'main-canopies': (title, pd, trail, price) => {
    const brand = getBrandDisplay(trail[1]?.name);
    const model = getShortName(trail[2]?.name);
    const size = pd.main_reserve_size;

    const headerParts = ['Buy used'];
    if (brand && model) {
      headerParts.push(brand, model);
    } else {
      headerParts.push(title);
    }
    if (size) headerParts.push(`${size} sqft`);
    let desc = headerParts.join(' ') + '.';

    const details = [];
    if (pd.year) details.push(`DOM ${pd.year}`);
    if (pd.number_of_jumps) details.push(`${pd.number_of_jumps} jumps`);
    if (Array.isArray(pd.color) && pd.color.length > 0) {
      details.push(pd.color.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(', '));
    }
    if (details.length > 0) desc += ` ${details.join(', ')}.`;

    if (price) desc += ` ${price}.`;
    const loc = formatLocation(pd.location);
    if (loc) desc += ` ${loc}`;
    desc += ' Buy with escrow payments.';
    return desc;
  },

  // Reserves: PD display override, no jumps
  'reserves': (title, pd, trail, price) => {
    const brand = getBrandDisplay(trail[1]?.name);
    const model = getShortName(trail[2]?.name);

    const headerParts = ['Buy used'];
    if (brand && model) {
      // Avoid "PD PD Reserve" duplication
      if (model.toLowerCase().startsWith(brand.toLowerCase())) {
        headerParts.push(model);
      } else {
        headerParts.push(brand, model);
      }
    } else if (brand) {
      headerParts.push(brand, title);
    } else {
      headerParts.push(title);
    }
    if (pd.main_reserve_size) headerParts.push(`${pd.main_reserve_size} sqft`);
    let desc = headerParts.join(' ') + '.';

    if (pd.year) desc += ` DOM ${pd.year}.`;
    if (price) desc += ` ${price}.`;
    const loc = formatLocation(pd.location);
    if (loc) desc += ` ${loc}`;
    desc += ' Buyer protection with escrow payments.';
    return desc;
  },

  // AADs: drop brand, keep seller title + year + type context
  'aad': (title, pd, trail, price) => {
    let desc = `Used ${title}`;
    if (pd.year) desc += `, DOM ${pd.year}`;
    desc += '. Skydiving AAD.';
    if (price) desc += ` ${price}.`;
    const loc = formatLocation(pd.location);
    if (loc) desc += ` ${loc}`;
    desc += ' Buy with safe payments on Avento.';
    return desc;
  },

  // Altimeters: type label varies by L2 subtype
  'altimeters': (title, pd, trail, price) => {
    const l2Id = trail[1]?.id;
    let typeLabel = 'skydiving altimeter';
    if (l2Id === 'altimeters-digital') typeLabel = 'Digital Altimeter';
    else if (l2Id === 'altimeters-analogue') typeLabel = 'Analogue Altimeter';
    else if (l2Id === 'altimeters-mounts') typeLabel = 'Altimeter Mount';
    return defaultDescription(title, pd, trail, price, typeLabel);
  },

  // Audibles: "Audible Altimeter, Ditter" as type context
  'audibles': (title, pd, trail, price) => {
    return defaultDescription(title, pd, trail, price, 'Audible Altimeter, Ditter');
  },

  // Wingsuits: custom format, strips brand + "Wingsuit" from seller title
  'wingsuits': (title, pd, trail, price) => {
    const brand = getBrandDisplay(trail[1]?.name);
    let clean = title;
    if (brand) clean = stripBrandFromTitle(clean, brand);
    clean = stripTerm(clean, 'wingsuit');

    const headerParts = ['Buy used'];
    if (brand) headerParts.push(brand);
    if (clean) headerParts.push(clean);
    headerParts.push('Wingsuit');
    let desc = headerParts.join(' ') + '.';

    const details = [];
    if (pd.number_of_jumps) details.push(`${pd.number_of_jumps} jumps`);
    if (pd.year) details.push(`DOM ${pd.year}`);
    if (details.length > 0) desc += ` ${details.join(', ')}.`;

    if (price) desc += ` ${price}.`;
    const loc = formatLocation(pd.location);
    if (loc) desc += ` ${loc}`;
    desc += ' Buy with safe payments on Avento.';
    return desc;
  },

  // Tracksuits: same as wingsuits with {L2 Type} Tracksuit
  'tracksuits': (title, pd, trail, price) => {
    const l2Name = trail[1]?.name;
    const brand = getBrandDisplay(trail[2]?.name);
    let clean = title;
    if (brand) clean = stripBrandFromTitle(clean, brand);
    clean = stripTerm(clean, 'tracksuit');

    const headerParts = ['Buy used'];
    if (brand) headerParts.push(brand);
    if (clean) headerParts.push(clean);
    if (l2Name) headerParts.push(l2Name);
    headerParts.push('Tracksuit');
    let desc = headerParts.join(' ') + '.';

    const details = [];
    if (pd.number_of_jumps) details.push(`${pd.number_of_jumps} jumps`);
    if (pd.year) details.push(`DOM ${pd.year}`);
    if (details.length > 0) desc += ` ${details.join(', ')}.`;

    if (price) desc += ` ${price}.`;
    const loc = formatLocation(pd.location);
    if (loc) desc += ` ${loc}`;
    desc += ' Buy with safe payments on Avento.';
    return desc;
  },

  // Helmets: type label depends on L2 subtype
  'helmets-and-equipment': (title, pd, trail, price) => {
    const l2Id = trail[1]?.id;

    if (l2Id === 'helmet-and-equipment-helmet-equipment') {
      // Use L3 value (Helmet Bags, Helmet Visors, etc.) instead of "helmet"
      const l3Name = getShortName(trail[2]?.name);
      const condition = getConditionLabel(pd.condition);
      let desc = `${condition} ${title}`;
      const details = [];
      if (l3Name) details.push(l3Name);
      if (pd.helmets_size) details.push(`size ${pd.helmets_size}`);
      if (pd.helmets_nr_of_jumps) details.push(`${pd.helmets_nr_of_jumps} jumps`);
      if (pd.year) details.push(`DOM ${pd.year}`);
      if (details.length > 0) desc += `, ${details.join(', ')}`;
      desc += '.';
      if (price) desc += ` ${price}.`;
      const loc = formatLocation(pd.location);
      if (loc) desc += ` ${loc}`;
      desc += ' Buy with safe payments on Avento.';
      return desc;
    }

    if (l2Id === 'helmets-and-equipment-glasses') {
      // No "helmet" at all
      return defaultDescription(title, pd, trail, price, null);
    }

    // Helmets subtype: "skydiving helmet"
    return defaultDescription(title, pd, trail, price, 'skydiving helmet');
  },

  // Video/Photo: drop type label from description
  'videography-photography': (title, pd, trail, price) => {
    return defaultDescription(title, pd, trail, price, null);
  },

  // Other Gear: add "Skydiving {L2}" as a detail instead of prefix label
  'other-gear': (title, pd, trail, price) => {
    const condition = getConditionLabel(pd.condition);
    const l2Name = getShortName(trail[1]?.name);

    let desc = `${condition} ${title}`;
    const details = [];
    if (l2Name) details.push(`Skydiving ${l2Name}`);
    if (pd.number_of_jumps) details.push(`${pd.number_of_jumps} jumps`);
    const year = pd.year || pd.year_rig;
    if (year) details.push(`DOM ${year}`);
    if (details.length > 0) desc += `, ${details.join(', ')}`;
    desc += '.';

    if (price) desc += ` ${price}.`;
    const loc = formatLocation(pd.location);
    if (loc) desc += ` ${loc}`;
    desc += ' Buy with safe payments on Avento.';
    return desc;
  },
};

// ─── Exported API ────────────────────────────────────────────────────────────

/**
 * Build SEO-enhanced page title for a listing.
 * Dispatches to per-category builder, falls back to generic.
 */
export const buildListingSeoTitle = (title, publicData = {}, categoryTrail = []) => {
  if (!title) return 'Skydiving Gear for Sale | Avento';
  const l1Id = categoryTrail[0]?.id;
  const builder = l1Id && TITLE_BUILDERS[l1Id];
  if (builder) return builder(title, publicData, categoryTrail);
  return defaultTitle(title, publicData, categoryTrail);
};

/**
 * Alt-text base for listing gallery photos, derived from the SEO title:
 * drops the "| Avento" suffix, reads remaining "|" separators as commas.
 * The gallery appends ", photo {n} of {count}".
 */
export const buildListingImageAltBase = seoTitle =>
  seoTitle.replace(/\s*\|\s*Avento\s*$/, '').replace(/\s*\|\s*/g, ', ');

/**
 * Build SEO-enhanced meta description for a listing.
 * Dispatches to per-category builder, falls back to generic.
 */
export const buildListingSeoDescription = (
  title,
  publicData = {},
  categoryTrail = [],
  formattedPrice
) => {
  if (!title) return 'Skydiving gear for sale. Buy with safe payments on Avento.';
  const l1Id = categoryTrail[0]?.id;
  const builder = l1Id && DESC_BUILDERS[l1Id];
  if (builder) return builder(title, publicData, categoryTrail, formattedPrice);
  return defaultDescription(title, publicData, categoryTrail, formattedPrice);
};

/**
 * Build OfferShippingDetails entries for the Product schema.
 * Mirrors the merchantSync mapShipping logic: for each target country, pick
 * national / continental / global rate based on origin country's continent.
 *
 * Returns one OfferShippingDetails entry per shippable target country, or an
 * empty array if shipping is not enabled.
 */
const buildShippingDetails = (publicData, priceCurrency) => {
  if (!publicData?.shippingEnabled || !publicData?.shippingPrices) return [];

  const rawOrigin = publicData.shippingOrigin?.countryCode || publicData.location?.countryCode;
  const originCountry = typeof rawOrigin === 'string' ? rawOrigin.trim().toUpperCase() : null;
  const { shippingPrices } = publicData;
  const national = shippingPrices.national?.oneItem;
  const continental = shippingPrices.continental?.oneItem;
  const global = shippingPrices.global?.oneItem;

  // Respect the seller's allowed destinations (same filtering as merchantSync).
  const shippingCountries = publicData.shippingCountries;
  const allowedSet =
    Array.isArray(shippingCountries) && shippingCountries.length > 0
      ? new Set(
          shippingCountries
            .map(c => (typeof c === 'string' ? c.trim().toUpperCase() : ''))
            .filter(Boolean)
        )
      : null;
  const allowedTargets = allowedSet
    ? GMC_TARGET_COUNTRIES.filter(country => allowedSet.has(country))
    : GMC_TARGET_COUNTRIES;

  const deliveryTime = {
    '@type': 'ShippingDeliveryTime',
    handlingTime: {
      '@type': 'QuantitativeValue',
      minValue: 0,
      maxValue: 7,
      unitCode: 'd',
    },
    transitTime: {
      '@type': 'QuantitativeValue',
      minValue: 1,
      maxValue: 14,
      unitCode: 'd',
    },
  };

  // Shipping prices are stored in the seller's currency subunits; zero-decimal
  // currencies (JPY, ISK, ...) have divisor 1, not 100.
  const currency = priceCurrency || publicData.sellerCurrency;
  const divisor = subUnitDivisors[currency] || 100;
  const toMajor = subunits => (Number(subunits) / divisor).toFixed(divisor === 1 ? 0 : 2);

  // Pick the applicable rate per country (same tier logic as merchantSync).
  return allowedTargets
    .map(country => {
      let price = null;
      if (country === originCountry && national != null) {
        price = national;
      } else if (areCountriesInSameContinent(country, originCountry) && continental != null) {
        price = continental;
      } else if (global != null) {
        price = global;
      }
      if (price == null) return null;
      return {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: toMajor(price),
          currency,
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: country,
        },
        deliveryTime,
      };
    })
    .filter(Boolean);
};

/**
 * Build enhanced schema.org Product structured data.
 * Adds sku, brand, model, category, size, jumps, year, and location.
 */
export const buildEnhancedProductSchema = ({
  description,
  publicData = {},
  categoryTrail = [],
  schemaImages,
  productURL,
  priceSchemaMaybe,
  availabilityMaybe,
  itemConditionMaybe,
  seoTitle,
  sellerName,
  listingId,
}) => {
  const { brand: brandName, model: modelName } = getBrandAndModel(categoryTrail, publicData);
  const l1Name = categoryTrail.length > 0 ? categoryTrail[0].name : null;

  const brandMaybe = brandName
    ? { brand: { '@type': 'Brand', name: brandName } }
    : {};
  const modelMaybe = modelName ? { model: modelName } : {};
  const categoryMaybe = l1Name ? { category: l1Name } : {};

  const additionalProperty = [];

  const size =
    publicData.main_reserve_size || publicData.completesystem_main_canopy_size;
  if (size) {
    additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'Size',
      value: `${size} sqft`,
      unitCode: 'FTK',
    });
  }

  if (publicData.allowedRangeMin && publicData.allowedRangeMax) {
    additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'Holds Canopy Size',
      value: `${publicData.allowedRangeMin}\u2013${publicData.allowedRangeMax} sqft`,
    });
  }

  const jumps =
    publicData.number_of_jumps ||
    publicData.helmets_nr_of_jumps ||
    publicData.system_main_jumpnr;
  if (jumps) {
    additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'Number of Jumps',
      value: String(jumps),
    });
  }

  const year = publicData.year || publicData.year_rig;
  if (year) {
    additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'Year of Manufacture',
      value: String(year),
    });
  }

  const additionalPropertyMaybe =
    additionalProperty.length > 0 ? { additionalProperty } : {};

  const location = publicData.location;
  const availableAtMaybe = location?.countryCode
    ? {
        availableAtOrFrom: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressCountry: location.countryCode,
          },
        },
      }
    : {};

  const sellerMaybe = sellerName
    ? { seller: { '@type': 'Person', name: sellerName } }
    : {};

  const marketplaceRootURL = new URL(productURL).origin;

  // sku must stay identical to the GMC feed offerId (merchantSync.js) so Google
  // can match this page to the Merchant Center offer.
  const skuMaybe = listingId ? { sku: listingId } : {};

  const productSchema = {
    '@type': 'Product',
    description: description,
    name: seoTitle,
    image: schemaImages,
    ...skuMaybe,
    ...brandMaybe,
    ...modelMaybe,
    ...categoryMaybe,
    ...additionalPropertyMaybe,
    offers: {
      '@type': 'Offer',
      url: productURL,
      ...priceSchemaMaybe,
      ...availabilityMaybe,
      ...itemConditionMaybe,
      ...availableAtMaybe,
      ...sellerMaybe,
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: GMC_TARGET_COUNTRIES,
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
        merchantReturnLink: `${marketplaceRootURL}/p/return-policy`,
      },
    },
  };

  const shippingDetails = buildShippingDetails(publicData, priceSchemaMaybe?.priceCurrency);
  if (shippingDetails.length > 0) {
    productSchema.offers.shippingDetails = shippingDetails;
  }

  const breadcrumb = buildListingBreadcrumbSchema(categoryTrail, seoTitle, productURL);
  return breadcrumb ? [productSchema, breadcrumb] : productSchema;
};

/**
 * Build a BreadcrumbList schema for a listing page.
 * Home > L1 Category > L2 Category > ... > Listing Title
 */
export const buildListingBreadcrumbSchema = (categoryTrail = [], listingTitle, listingURL) => {
  if (categoryTrail.length === 0) return null;

  const marketplaceRootURL = new URL(listingURL).origin;
  const items = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: marketplaceRootURL },
  ];

  // Build category breadcrumb items with cumulative URL paths
  const categoryIds = categoryTrail.map(c => c.id);
  categoryTrail.forEach((cat, index) => {
    const pathSegments = categoryIds.slice(0, index + 1).join('/');
    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name: cat.name,
      item: `${marketplaceRootURL}/s/${pathSegments}`,
    });
  });

  // Add listing as final breadcrumb
  items.push({
    '@type': 'ListItem',
    position: categoryTrail.length + 2,
    name: listingTitle,
    item: listingURL,
  });

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};
